import { FC, useMemo } from 'react';

import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';

import {
  getCouponShareStatusLabel,
  getCouponShareStatusStyle,
} from '../utils/giftOrderCoupon.utils';

interface CouponRowProps {
  coupon: GiftCouponForStore;
  onShare: (coupon: GiftCouponForStore) => void;
  onMemo: (coupon: GiftCouponForStore) => void;
  isSharing: boolean;
}

const formatConfirmedAt = (date: Date | string) => {
  const d = new Date(date);
  return `${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} 완료`;
};

export const CouponRow: FC<CouponRowProps> = ({ coupon, onShare, onMemo, isSharing }) => {
  const giftItem = coupon.giftItem;
  const giftItemInfo = typeof giftItem === 'object' ? giftItem?.giftItemInfo : null;

  const imageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo]);

  const isShared = coupon.shareStatus !== GiftCouponShareStatus.NotShared;

  const isRedeemed =
    coupon.redeemStatus === GiftCouponRedeemStatus.Delivered ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Refunded ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Canceled;

  const shareDisabled = isRedeemed || coupon.shareStatus === GiftCouponShareStatus.Viewed;

  const itemName =
    typeof giftItem === 'object' ? giftItem?.name || giftItemInfo?.name : '';

  return (
    <div
      className={`flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm ${
        isShared ? 'ring-1 ring-green-400' : ''
      }`}
    >
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {imageUrl ? (
          <img
            src={fileBucketUrl(imageUrl, 'w200')}
            alt={itemName || '쿠폰'}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
            No Image
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">#{coupon.seq}</span>
          <span className="text-xs text-gray-600">{coupon.code}</span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${getCouponShareStatusStyle(
              coupon.shareStatus,
            )}`}
          >
            {getCouponShareStatusLabel(coupon.shareStatus)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-sm font-medium text-gray-800">{itemName}</p>
      </div>

      {isShared && coupon.confirmedAt ? (
        <div className="flex flex-shrink-0 flex-col items-end gap-0.5">
          {coupon.memo ? (
            <span className="truncate rounded-lg bg-purple-50 px-3 py-1 text-xs font-medium text-purple-600">
              {coupon.memo}
            </span>
          ) : (
            <button
              onClick={() => onMemo(coupon)}
              className="rounded-lg border border-dashed border-purple-300 px-3 py-1 text-xs text-purple-400 active:bg-purple-50"
            >
              메모 남기기
            </button>
          )}
          <span className="text-[10px] text-gray-400">
            {formatConfirmedAt(coupon.confirmedAt)}
          </span>
        </div>
      ) : (
        !shareDisabled && (
          <button
            onClick={() => onShare(coupon)}
            disabled={isSharing}
            className={`flex-shrink-0 rounded-xl px-5 py-2.5 text-sm font-bold transition ${
              isShared
                ? 'bg-green-100 text-green-700'
                : 'bg-purple-600 text-white active:bg-purple-700'
            } ${isSharing ? 'opacity-50' : ''}`}
          >
            {isSharing ? '...' : isShared ? '다시 전달' : '전달하기'}
          </button>
        )
      )}
    </div>
  );
};
